// VUE INITIALIZATION LOGIC MIXIN
import {CANCELLED_QUERY_PARAM_KEY, CANCELLED_QUERY_PARAM_VALUE} from '@/js/funnel/_mixins/urls';
import {isLocalEnv, canViewTestCode} from '@/js/global/config';
import {mapGetters, mapState} from 'vuex';
import moment from 'moment-timezone';
export const InitMixin = {
   data() {
      return {
         currentUserId: null,
         cartErrors: {},
         isTemplateDataLoaded: false,
         isCookieDataLoaded: false,
         isInitProtocolFinished: false,
         addresses: {},
         fields: {},
         isCancellationRequest: false,
      }
   },
   created() {
      if (window.mymy.vueLocal) {
         // add our form fields object to the local data store for form reactivity
         this.initFormFields(window.mymy.vueLocal.fields ?? {});

         // isolate address data (even though they are form fields too)
         this.initCustomerAddresses(window.mymy.vueLocal.addresses ?? {});
      }
   },
   mounted() {
      // [1/3]: Add the menu data to the store.
      try {
         this.$store.commit('initTemplateStoreData');
         this.isTemplateDataLoaded = true;
      }
      catch (e) {
         if(window.Sentry) {
            Sentry.captureException(e);
         }
         console.error(e);
      }

      // [2/3]: Update the menu data in the store with the cookie data.
      this.initMenuCookieCheck();
      this.isCookieDataLoaded = true;

      // [3/3]: Update the store with the customer's cart data. This will run asynchronously
      // without waiting, so make sure nothing below this depends on the cart.
      this.$store.commit('isCartDataLoaded', false);
      this.fetchCart()
         .then(cart => {
            if (cart) {
               // update the Vuex store with the cart's data
               this.$store.commit('initCartStoreData', cart);

               // remember the cart number via the menu cookie
               this.setMenuCookie({ number: cart.number });
            }
            return cart;
         })
         .then(cart => {
            if (cart) {
               this.initFormFields(cart.mymy.fields);
               this.initCustomerAddresses(cart.mymy.addresses);
               this.modals.contactForm.fields.email = cart.email;
               this.$set(this.$data, 'urlParams', {...cart.mymy.urlParams});
            }
            return cart;
         })
         .then(cart => this.initCheckoutPageData(cart))
         .then(cart => this.initMarketing(cart))
         .finally(cart => {
            // tell Vue the cart has been loaded
            this.$store.commit('isCartDataLoaded', true);
            this.modals.loading.show = false;
            return cart;
         });

      // announce that all the important init protocol is done
      this.isInitProtocolFinished = true;
   },

   computed: {
      /**
       * These computed properties map to properties in our Vuex `state.js` file.
       */
      ...mapState({
         cart: 'cart',
         focusedMenuItem: 'focusedMenuItem',
         lineItems: state => state.summary.lineItems,
         menu: 'menu',
         sectionIndex: state => state.menu.sectionIndex,
         summary: 'summary',
         summaryCourseItems: state => state.summary.courseItems,
         isCartDataLoaded: 'isCartDataLoaded'
      }),
      /**
       * These computed properties map to identical methods in our Vuex `getters.js` file.
       */
      ...mapGetters({
         isDeliveryPage: 'isDeliveryPage',
         isMinimumOrderValueMet: 'isMinimumOrderValueMet',
         isOverviewPage: 'isOverviewPage',
         isSelectionPage: 'isSelectionPage',
         totalMenuSections: 'totalMenuSections',
      }),

      /**
       * Is the application ready to start functioning now that the menu and cart data has been
       * loaded into the Vuex store?
       *
       * @return {boolean}
       */
      isReady() {
         return this.isTemplateDataLoaded &&
            this.isCookieDataLoaded &&
            this.isCartDataLoaded &&
            this.isInitProtocolFinished;
      },

      /**
       * Is the customer currently logged into a Craft `User` account?
       *
       * @return {boolean}
       * @see https://craftcms.com/docs/3.x/dev/global-variables.html#currentuser
       */
      isCurrentUser() {
         return typeof this.currentUserId === 'number';
      },

      /**
       * Get the static translations from the window object.
       *
       * @return {*}
       */
      translations() {
         return window.mymy.translations;
      },

      /**
       * Can this environment view test code developers output to the console?
       *
       * @return {boolean}
       */
      canViewTestCode: () => canViewTestCode(),

      /**
       * If errors found in the cart's validation.
       *
       * @return {boolean}
       */
      cartErrorsExist() {
         // don't block the customer's view of the selection page if/when there are cart errors
         // since the page is public and it will confuse the customer.
         if (this.isSelectionPage) {
            return false;
         }

         return Object.keys(this.cartErrors).length > 0;
      },
   },

   methods: {
      /**
       * Add our form fields object to the local data store for form reactivity.
       *
       * @param fields {object}
       */
      initFormFields(fields) {
         for (let handle in fields) {
            try {
               this.$set(this.fields, handle, fields[handle]);
            }
            catch (e) {
               if(window.Sentry) {
                  Sentry.captureException(e);
               }
               console.error(e);
            }
         }
      },

      /**
       * Isolate address data (even though they are form fields too).
       *
       * @param addresses
       */
      initCustomerAddresses(addresses) {
         for (let handle in addresses) {
            try {
               this.$set(this.addresses, handle, addresses[handle]);
            }
            catch (e) {
               if(window.Sentry) {
                  Sentry.captureException(e);
               }
               console.error(e);
            }
         }
      },

      /**
       * Handle anything related to marketing's needs (like Google Tag Manager, for example).
       *
       * @param cart {object}
       * @return {object}
       */
      initMarketing(cart) {
         if (! cart) {
            return cart;
         }

         // push the cart number into the dataLayer for TraceDock (now unused service) to catch and monitor
         if (window.dataLayer && cart && cart.shortNumber) {
            window.dataLayer.push({orderNumber: cart.shortNumber});
         }

         // gtm event
         if (this.isDeliveryPage) {
            this.gtmBeginCheckout();
         }

         return cart;
      },

      /**
       * Configure some special stuff for the overview page.
       *
       * @param cart {object}
       * @return {object}
       */
      initCheckoutPageData(cart) {
         if (! this.isOverviewPage && ! this.isDeliveryPage) {
            return cart;
         }

         // tell the customer the need to have a cart first before entering the checkout pages
         if (! cart) {
            this.cartErrors = {
               selection: [this.translations.cartErrors.errors.selection],
            };

            return cart;
         }


         // Check not fullfilled cart
         if(!this.isMinimumOrderValueMet) {
            if(window.Sentry) {
               Sentry.captureMessage("Cart not fullfilled in checkout ["+ cart.number +"]")
            }
         }

         // nothing left to do for the Delivery page
         if (! this.isOverviewPage) {
            return cart;
         }

         // the customer's delivery date/time properly formatted and translated
         this.deliveryDateWithTimeWindow = cart.mymy.deliveryDateWithTimeWindow ?? '';

         // store the url param data from the cart and check for some important booleans
         const urlParams = new URLSearchParams(window.location.search);
         this.isCancellationRequest = urlParams.get(CANCELLED_QUERY_PARAM_KEY) === CANCELLED_QUERY_PARAM_VALUE;

         // set this data now, which will be reused by several methods for handling different
         // payment gateways
         this.dataToSubmit = {
            redirect: this.getFinalCheckoutUrl(true),
            cancelUrl: this.getCancelledCheckoutUrl(true),
         };

         // if payment gateway is Stripe, initialize it (same goes for PayPal)
         if (!this.isCancellationRequest) {
            this._initStripe();
            this._initPayPal();
         }

         // are we coming back from Stripe or PayPal after authentication failed?
         if (this.isCancellationRequest) {
            // remove the "cancelled" query param from the URL
            this.removeUrlParam(CANCELLED_QUERY_PARAM_KEY);

            if (this.cartHasCardGateway && typeof this.fields.payment.stripe.errorMessage === 'string') {
               this.showErrorModal(this.fields.payment.stripe.errorMessage);
            }
            else if (this.cartHasPayPalGateway && this.translations.responses.transactionCancelled) {
               this.showErrorModal(this.fields.payment.stripe.transactionCancelled);
            }
         }

         // gtm event
         this.gtmAddPaymentInfo();

         return cart;
      },

      /**
       * Get data from the customer's cart related to the current menu they are viewing.
       *
       * Scenarios:
       *  1. Customer arrives on selection page with NO CART in their session.
       *       -> After the customer submits their selection, Commerce will create a new cart
       *          in their session and return the cart number, which will then be saved in the
       *          menu cookie.
       *  2. Customer has a cart BUT its menu DOESN'T exist or match the menu they are viewing.
       *       -> Continue as if they have no cart and follow #1 above.
       *  3. Customer has a cart AND its menu DOES match the menu they are viewing.
       *       -> Get the existing cart from Commerce and let the customer continue where
       *          they left off, using the data from the cart.
       *
       * @return {Promise<void>}
       */
      async fetchCart() {
         // DEFAULTS
         // =====================================================================================

         let menuCookie = this.getMenuCookie();
         let response = {data: {cart: null, currentUserId: null}};
         let cartNumber = null;

         // CHECK FOR AN EXISTING CART FOR THIS MENU
         // =====================================================================================

         // check for a cart number in the cookie, and get the cart if so
         if (menuCookie && menuCookie.n && typeof menuCookie.n === 'string') {
            this.modals.loading.show = true;
            cartNumber = menuCookie.n;
            response = await this.$store.dispatch('getCartByNumber', cartNumber);

            if(response?.data?.success) {
               this.currentUserId = response.data.currentUserId;
            }
            else if (typeof response?.data?.errors === 'object' && response?.data?.errors !== null && ! Array.isArray(response?.data?.errors)) {
               this.cartErrors = response.data.errors;
            }
            else {
               if(window.Sentry) {
                  Sentry.captureMessage("Response ("+ response.status +") not handledcorrectly :339")
               }
            }
         }

         // log the outcome to the console
         if (this.canViewTestCode) {
            if (response.data.cart) {
               console.log('✅', 'Cart "' + response.data.cart.shortNumber + '" exists:', response.data.cart);
            }
            else {
               if (cartNumber) {
                  console.log('❌', 'No cart exists with number:', cartNumber);
               }
               else {
                  console.log('❌', 'No cart exists yet for this menu.');
               }
            }
         }

         // nothing left to do without a cart
         if (! response.data.cart) {
            this.modals.loading.show = false;
            return null;
         }


         // ENSURE THE EXISTING CART RELATES TO THIS MENU
         // =====================================================================================

         // if the cart's `relatedMenu` doesn't match the current menu the customer is viewing,
         // continue like they have no cart.
         let cartMenuVariantId = null;
         try {
            cartMenuVariantId = response.data.cart.mymy.relatedMenu.variantId;
            if (cartMenuVariantId !== window.mymy.menu.variantId) {
               throw new Error("cart's related menu doesn't match this menu.");
            }
         }
         catch (e) {
            if (this.canViewTestCode) {
               console.log('❌', "The cart doesn't have this menu set as its 'relatedMenu'.", {
                  cartMenuVariantId: cartMenuVariantId,
                  templateMenuVariantId: window.mymy.menu.variantId,
               });
            }
            return null;
         }

         // expose to developers for debugging
         if (this.canViewTestCode) {
            console.log('✅', 'The cart is related to this menu.', {
               cartMenuVariantId: cartMenuVariantId,
               templateMenuVariantId: window.mymy.menu.variantId,
            });
         }

         try {
            if(response?.data?.cart?.mymy?.fields?.delivery?.date) {
               let castedDate = moment(response?.data?.cart?.mymy?.fields?.delivery?.date)
               if(castedDate.isValid()) {
                  response.data.cart.mymy.fields.delivery.date = castedDate.toDate();
                  console.log('✅', 'Cast delivery date to Date')
               } else {
                  console.log('Unable to cast delivery date', response?.data?.cart?.mymy)
               }
            }
         } catch(e) {
            console.log('Caught error casting date')
            console.error(e)
            if(window.Sentry) {
               Sentry.captureException(e);
            }
         }

         return response.data.cart;
      },
   },
};